// ============================================
// COSMIC HINDU - Discount Context
// Loads discount rules from PHP API and exposes helper functions.
// ============================================

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import discountService from '@services/discountService';

const DiscountContext = createContext(null);

export function DiscountProvider({ children }) {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    discountService.getAll()
      .then((res) => setDiscounts(res.data || []))
      .catch(() => setDiscounts([]))
      .finally(() => setLoading(false));
  }, []);

  // Returns the best discounted price for a product
  const discountedPrice = useCallback((price, categoryId) => {
    if (!discounts.length) return price;
    return discountService.applyToPrice(price, discounts, categoryId);
  }, [discounts]);

  // Returns discount % for display on product cards
  const discountPercent = useCallback((price, categoryId) => {
    const discounted = discountedPrice(price, categoryId);
    if (discounted >= price) return 0;
    return Math.round(((price - discounted) / price) * 100);
  }, [discountedPrice]);

  // Apply best sitewide or category discount to a cart total
  // (returns { discountedTotal, savedAmount, appliedRule })
  const applyToCart = useCallback((cartTotal, cartItems = []) => {
    if (!discounts.length) return { discountedTotal: cartTotal, savedAmount: 0, appliedRule: null };

    // Gather all applicable rules (sitewide only, not per-item at cart level)
    const sitewideRules = discounts.filter(
      (d) => d.active && d.scope === 'all' && d.type !== 'coupon'
    );
    if (!sitewideRules.length) return { discountedTotal: cartTotal, savedAmount: 0, appliedRule: null };

    let bestSave = 0;
    let appliedRule = null;
    for (const rule of sitewideRules) {
      if (rule.min_order > 0 && cartTotal < rule.min_order) continue;
      const save = rule.type === 'percentage'
        ? (cartTotal * rule.value) / 100
        : rule.value;
      if (save > bestSave) { bestSave = save; appliedRule = rule; }
    }

    return {
      discountedTotal: Math.max(0, cartTotal - bestSave),
      savedAmount: bestSave,
      appliedRule,
    };
  }, [discounts]);

  // Validate a coupon code against live rules
  const validateCoupon = useCallback(async (code, orderTotal) => {
    return discountService.validateCoupon(code, orderTotal);
  }, []);

  return (
    <DiscountContext.Provider value={{ discounts, loading, discountedPrice, discountPercent, applyToCart, validateCoupon }}>
      {children}
    </DiscountContext.Provider>
  );
}

export const useDiscounts = () => {
  const ctx = useContext(DiscountContext);
  if (!ctx) throw new Error('useDiscounts must be used within DiscountProvider');
  return ctx;
};

export default DiscountContext;
